/**
 * LayersPanel — panel de capas map-céntrico (primitiva S.UI.P3).
 *
 * Presentacional puro: sin lógica de negocio, sin estado interno y sin Leaflet.
 * Recibe el modelo de capas (`capas`) y emite callbacks (`onToggleVisible`,
 * `onOpacidad?`, `onReordenar?`); no carga ni gestiona capas WMS — eso vive en el
 * consumidor (montaje en P2/P3, tarea A3c). Para la ranura `panelIzq` de
 * `WorkspaceShell` (S.UI.P1); no se monta aún (DoD por primitiva — Bloque P de
 * PLAN_SIMPLIF_UI_P1P2P3). Skin DS v2 (nombres en Space Grotesk, IDs en JetBrains
 * Mono); sin glow ni box-shadow (regla dura).
 *
 * @version 1.0.0
 */

import type { CSSProperties, FC } from 'react';
import { colors, typography } from '@/design-system/v2';

// ── Tokens DS v2 ─────────────────────────────────────────────────────────────
const COLORS = {
  bg: colors.bg.secondary,
  fgFuerte: colors.fg.primary,
  fgTenue: colors.fg.muted,
  borde: colors.border.DEFAULT,
  visible: colors.primary, // #2dd4bf — capa activa
  oculta: colors.fg.muted, // capa oculta
} as const;

const OPACIDAD_DEF = 1;

// ── Tipos ────────────────────────────────────────────────────────────────────
/** Naturaleza de la capa, para etiquetado o agrupación por el consumidor. */
export type TipoCapa = 'base' | 'overlay' | 'infra';

/** Modelo presentacional mínimo de una capa del panel. */
export interface CapaLayer {
  /** Identificador estable de la capa (se muestra en mono). */
  id: string;
  /** Nombre legible de la capa (se muestra en sans). */
  nombre: string;
  /** Si la capa está visible en el mapa. */
  visible: boolean;
  /** Naturaleza de la capa (base/overlay/infra). Def: 'overlay'. */
  tipo?: TipoCapa;
  /** Opacidad 0–1 (solo se usa si se provee `onOpacidad`). Def: 1. */
  opacidad?: number;
}

export interface LayersPanelProps {
  /** Lista de capas a mostrar (orden de arriba abajo). */
  capas: CapaLayer[];
  /** Callback al alternar la visibilidad de una capa. */
  onToggleVisible: (id: string) => void;
  /** Callback de opacidad; si se omite, no se muestra el control de opacidad. */
  onOpacidad?: (id: string, valor: number) => void;
  /** Callback de reordenación; si se omite, no se muestran las flechas. */
  onReordenar?: (id: string, direccion: 'subir' | 'bajar') => void;
  /** Título del panel. Def: 'Capas'. */
  titulo?: string;
  /** Clase CSS adicional. */
  className?: string;
}

// ── Estilos ──────────────────────────────────────────────────────────────────
const raizStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', minHeight: 0,
  padding: '8px', background: COLORS.bg, color: COLORS.fgFuerte,
  fontFamily: typography.fontFamily.sans, fontSize: typography.fontSize.sm,
};
const tituloStyle: CSSProperties = {
  margin: 0, padding: '2px 4px 6px', color: COLORS.fgTenue,
  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
  letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
};
const listaStyle: CSSProperties = {
  listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '2px',
};
const filaStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 6px',
  borderRadius: '6px', border: `1px solid ${COLORS.borde}`, minWidth: 0,
};
const toggleStyle = (visible: boolean): CSSProperties => ({
  appearance: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px',
  border: `1px solid ${COLORS.borde}`, borderRadius: '6px', background: 'transparent',
  color: visible ? COLORS.visible : COLORS.oculta, fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs, padding: '2px 8px', cursor: 'pointer', flexShrink: 0, // sin glow
});
const puntoStyle = (visible: boolean): CSSProperties => ({
  width: '8px', height: '8px', borderRadius: '50%',
  background: visible ? COLORS.visible : 'transparent',
  border: `1px solid ${visible ? COLORS.visible : COLORS.oculta}`,
});
const nombreStyle: CSSProperties = {
  flex: 1, minWidth: 0, color: COLORS.fgFuerte,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const idStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs,
  color: COLORS.fgTenue, flexShrink: 0,
};
const grupoFlechasStyle: CSSProperties = { display: 'inline-flex', gap: '2px', flexShrink: 0 };
const flechaStyle: CSSProperties = {
  appearance: 'none', border: `1px solid ${COLORS.borde}`, borderRadius: '4px',
  background: 'transparent', color: COLORS.fgFuerte, fontFamily: typography.fontFamily.mono,
  fontSize: typography.fontSize.xs, lineHeight: 1, padding: '1px 5px', cursor: 'pointer',
};
const opacidadStyle: CSSProperties = {
  width: '64px', flexShrink: 0, cursor: 'pointer', accentColor: COLORS.visible,
};
const vacioStyle: CSSProperties = {
  padding: '8px 6px', color: COLORS.fgTenue, fontStyle: 'italic', fontSize: typography.fontSize.xs,
};

// ── Subcomponente: fila de capa ──────────────────────────────────────────────
const FilaCapa: FC<{
  capa: CapaLayer;
  onToggleVisible: (id: string) => void;
  onOpacidad?: (id: string, valor: number) => void;
  onReordenar?: (id: string, direccion: 'subir' | 'bajar') => void;
}> = ({ capa, onToggleVisible, onOpacidad, onReordenar }) => {
  const { id, nombre, visible, opacidad = OPACIDAD_DEF } = capa;
  return (
    <li data-testid={`layers-panel-capa-${id}`} style={filaStyle}>
      <button
        type="button"
        data-testid={`layers-panel-toggle-${id}`}
        aria-pressed={visible}
        aria-label={`${visible ? 'Ocultar' : 'Mostrar'} capa ${nombre}`}
        style={toggleStyle(visible)}
        onClick={() => onToggleVisible(id)}
      >
        <span aria-hidden="true" style={puntoStyle(visible)} />
        {visible ? 'Visible' : 'Oculta'}
      </button>
      <span style={nombreStyle} title={nombre}>{nombre}</span>
      {onReordenar != null && (
        <span style={grupoFlechasStyle}>
          <button type="button" data-testid={`layers-panel-subir-${id}`}
            aria-label={`Subir capa ${nombre}`} style={flechaStyle}
            onClick={() => onReordenar(id, 'subir')}>↑</button>
          <button type="button" data-testid={`layers-panel-bajar-${id}`}
            aria-label={`Bajar capa ${nombre}`} style={flechaStyle}
            onClick={() => onReordenar(id, 'bajar')}>↓</button>
        </span>
      )}
      <span data-testid={`layers-panel-id-${id}`} style={idStyle}>{id}</span>
      {onOpacidad != null && (
        <input
          type="range" min={0} max={1} step={0.05} value={opacidad}
          data-testid={`layers-panel-opacidad-${id}`}
          aria-label={`Opacidad de la capa ${nombre}`}
          style={opacidadStyle}
          onChange={(e) => onOpacidad(id, Number(e.target.value))}
        />
      )}
    </li>
  );
};

// ── Componente ───────────────────────────────────────────────────────────────
export const LayersPanel: FC<LayersPanelProps> = ({
  capas, onToggleVisible, onOpacidad, onReordenar, titulo = 'Capas', className,
}) => {
  const claseRaiz = className ? `ptel-layers-panel ${className}` : 'ptel-layers-panel';
  return (
    <div className={claseRaiz} data-testid="layers-panel" style={raizStyle}>
      <h2 data-testid="layers-panel-titulo" style={tituloStyle}>{titulo}</h2>
      {capas.length === 0 ? (
        <p data-testid="layers-panel-vacio" style={vacioStyle}>No hay capas disponibles.</p>
      ) : (
        <ul data-testid="layers-panel-lista" style={listaStyle}>
          {capas.map((capa) => (
            <FilaCapa
              key={capa.id}
              capa={capa}
              onToggleVisible={onToggleVisible}
              onOpacidad={onOpacidad}
              onReordenar={onReordenar}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default LayersPanel;
